import './styles.css'
import { LotteryController } from './core/LotteryController'
import type { MachineId } from './core/types'
import { MachineWorld } from './physics/MachineWorld'
import { LotteryRenderer } from './render/LotteryRenderer'

const canvas = document.querySelector<HTMLCanvasElement>('#view')!
const appEl = document.querySelector('#app')!
const bootSplash = document.querySelector('#boot-splash')
const bootStatus = document.querySelector('#boot-status')
const stateEl = document.querySelector('#state')!
const progressEl = document.querySelector('#progress')!
const resultEl = document.querySelector('#result')!
const redBallsEl = document.querySelector('#red-balls')!
const blueBallsEl = document.querySelector('#blue-balls')!
const panel = document.querySelector('#panel')!
const btnStart = document.querySelector<HTMLButtonElement>('#btn-start')!
const btnCopy = document.querySelector<HTMLButtonElement>('#btn-copy')!
const btnHistory = document.querySelector<HTMLButtonElement>('#btn-history')!
const btnReset = document.querySelector<HTMLButtonElement>('#btn-reset')!
const btnToggle = document.querySelector<HTMLButtonElement>('#toggle-panel')!
const tabRed = document.querySelector<HTMLButtonElement>('#tab-red')!
const tabBlue = document.querySelector<HTMLButtonElement>('#tab-blue')!
const toastEl = document.querySelector<HTMLElement>('#toast')!
const copyModal = document.querySelector<HTMLElement>('#copy-modal')!
const copyFmtPretty = document.querySelector('#copy-fmt-pretty')!
const copyFmtCompact = document.querySelector('#copy-fmt-compact')!
const btnCopyPretty = document.querySelector<HTMLButtonElement>('#btn-copy-pretty')!
const btnCopyCompact = document.querySelector<HTMLButtonElement>('#btn-copy-compact')!
const historyModal = document.querySelector<HTMLElement>('#history-modal')!
const historyListEl = document.querySelector('#history-list')!
const btnHistoryClear = document.querySelector<HTMLButtonElement>('#btn-history-clear')!

const HISTORY_KEY = 'ssq-sim-history-v1'
const HISTORY_MAX = 20

interface HistoryItem {
  id: string
  time: number
  red: number[]
  blue: number[]
}

const STATE_LABEL: Record<string, string> = {
  Idle: '空闲',
  LoadBalls: '装球',
  DropIn: '落球入舱',
  MixWarmup: '双盘加速',
  Mixing: '搅拌中',
  ReadyToDraw: '准备出球',
  DrawOne: '底部出球',
  BallCaptured: '管道滚动',
  ShowResult: '展示结果',
  WindDown: '停盘',
  Finished: '完成',
  Error: '错误',
}

function setBootStatus(text: string): void {
  if (bootStatus) bootStatus.textContent = text
}

function dismissBootSplash(): void {
  appEl.classList.remove('is-booting')
  if (!bootSplash) return
  bootSplash.classList.add('is-done')
  bootSplash.setAttribute('aria-busy', 'false')
  window.setTimeout(() => bootSplash.remove(), 600)
}

setBootStatus('初始化渲染…')
const controller = new LotteryController({ timing: { mix: 8 } })
const renderer = new LotteryRenderer(canvas)

let idlePreview: MachineWorld | null = null
let shownRed: number[] = []
let shownBlue: number[] = []

function renderResultStrip(red: number[], blue: number[]): void {
  shownRed = [...red]
  shownBlue = [...blue]
  renderer.setPinnedResults(shownRed, shownBlue)

  const fill = (el: Element, nums: number[], count: number, kind: 'red' | 'blue') => {
    el.innerHTML = ''
    for (let i = 0; i < count; i++) {
      const n = nums[i]
      const span = document.createElement('span')
      span.className = n != null ? `result-ball ${kind}` : 'result-ball empty'
      span.textContent = n != null ? String(n).padStart(2, '0') : ''
      el.appendChild(span)
    }
  }
  fill(redBallsEl, shownRed, controller.config.redCount, 'red')
  fill(blueBallsEl, shownBlue, controller.config.blueCount, 'blue')
  updateResultText()
}

function updateResultText(): void {
  const redTxt = shownRed.filter((n) => n != null).join(', ') || '…'
  const blueTxt = shownBlue.filter((n) => n != null).join(', ') || '…'
  resultEl.textContent = `红 [${redTxt}] 蓝 [${blueTxt}]`
}

function getDrawnParts(): { red: number[]; blue: number[] } | null {
  const red = shownRed.filter((n) => n != null)
  const blue = shownBlue.filter((n) => n != null)
  if (!red.length && !blue.length) return null
  return { red, blue }
}

function formatPrettyFrom(red: number[], blue: number[]): string {
  const redPart = red.map((n) => String(n).padStart(2, '0')).join(' ')
  const bluePart = blue.map((n) => String(n).padStart(2, '0')).join(' ')
  if (redPart && bluePart) return `红球 ${redPart} + 蓝球 ${bluePart}`
  if (redPart) return `红球 ${redPart}`
  return `蓝球 ${bluePart}`
}

function formatCompactFrom(red: number[], blue: number[]): string {
  return [...red, ...blue].map((n) => String(n).padStart(2, '0')).join('')
}

function formatDrawnPretty(): string | null {
  const parts = getDrawnParts()
  if (!parts) return null
  return formatPrettyFrom(parts.red, parts.blue)
}

function formatDrawnCompact(): string | null {
  const parts = getDrawnParts()
  if (!parts) return null
  return formatCompactFrom(parts.red, parts.blue)
}

function loadHistory(): HistoryItem[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as HistoryItem[]
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (x) =>
          x &&
          typeof x.id === 'string' &&
          typeof x.time === 'number' &&
          Array.isArray(x.red) &&
          Array.isArray(x.blue),
      )
      .slice(0, HISTORY_MAX)
  } catch {
    return []
  }
}

function saveHistory(items: HistoryItem[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(items.slice(0, HISTORY_MAX)))
  } catch {
    // ignore quota / private mode
  }
}

function pushHistory(red: number[], blue: number[]): void {
  if (!red.length && !blue.length) return
  const next: HistoryItem = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    time: Date.now(),
    red: [...red],
    blue: [...blue],
  }
  const list = [next, ...loadHistory()].slice(0, HISTORY_MAX)
  saveHistory(list)
}

function formatHistoryTime(ts: number): string {
  const d = new Date(ts)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

function renderHistoryList(): void {
  const items = loadHistory()
  historyListEl.innerHTML = ''
  if (!items.length) {
    const empty = document.createElement('p')
    empty.className = 'history-empty'
    empty.textContent = '暂无历史记录'
    historyListEl.appendChild(empty)
    btnHistoryClear.hidden = true
    return
  }
  btnHistoryClear.hidden = false
  for (const item of items) {
    const row = document.createElement('div')
    row.className = 'history-item'

    const top = document.createElement('div')
    top.className = 'history-item-top'
    const time = document.createElement('span')
    time.className = 'history-item-time'
    time.textContent = formatHistoryTime(item.time)
    const copyBtn = document.createElement('button')
    copyBtn.type = 'button'
    copyBtn.className = 'ghost history-copy-btn'
    copyBtn.textContent = '复制'
    copyBtn.addEventListener('click', () => {
      openCopyModal(item.red, item.blue)
    })
    top.append(time, copyBtn)

    const nums = document.createElement('div')
    nums.className = 'history-item-nums'
    const redTxt = item.red.map((n) => String(n).padStart(2, '0')).join(' ')
    const blueTxt = item.blue.map((n) => String(n).padStart(2, '0')).join(' ')
    nums.innerHTML = `${redTxt || '—'}${blueTxt ? ` <span class="blue">+ ${blueTxt}</span>` : ''}`

    row.append(top, nums)
    historyListEl.appendChild(row)
  }
}

function openHistoryModal(): void {
  renderHistoryList()
  historyModal.hidden = false
}

function closeHistoryModal(): void {
  historyModal.hidden = true
}

/** 复制弹框当前数据源（当前开奖或某条历史） */
let copyModalSource: { red: number[]; blue: number[] } | null = null

function openCopyModal(red?: number[], blue?: number[]): void {
  const source =
    red && blue
      ? { red: [...red], blue: [...blue] }
      : getDrawnParts()
  if (!source || (!source.red.length && !source.blue.length)) {
    showToast('暂无已摇出的号码')
    return
  }
  copyModalSource = source
  copyFmtPretty.textContent = formatPrettyFrom(source.red, source.blue)
  copyFmtCompact.textContent = formatCompactFrom(source.red, source.blue)
  copyModal.hidden = false
}

function closeCopyModal(): void {
  copyModal.hidden = true
}

let toastTimer = 0
function showToast(message: string): void {
  toastEl.hidden = false
  toastEl.textContent = message
  toastEl.classList.add('is-show')
  window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => {
    toastEl.classList.remove('is-show')
    window.setTimeout(() => {
      toastEl.hidden = true
    }, 220)
  }, 1800)
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through
  }
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('readonly', '')
  ta.style.position = 'fixed'
  ta.style.left = '-9999px'
  document.body.appendChild(ta)
  ta.select()
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  document.body.removeChild(ta)
  return ok
}

function showIdlePreview(): void {
  idlePreview?.dispose()
  idlePreview = new MachineWorld('red', controller.config)
  renderer.bindWorld(idlePreview)
  controller.setViewMachine('red')
  tabRed.classList.add('active')
  tabBlue.classList.remove('active')
}

function setTab(id: MachineId): void {
  controller.setViewMachine(id)
  tabRed.classList.toggle('active', id === 'red')
  tabBlue.classList.toggle('active', id === 'blue')
  const world = controller.getActiveWorld() ?? (id === 'red' ? idlePreview : null)
  if (world) renderer.bindWorld(world)
}

controller.on((e) => {
  if (e.type === 'machine_state') {
    stateEl.textContent = STATE_LABEL[e.state] ?? e.state
  }
  if (e.type === 'mix_progress') {
    progressEl.textContent = `${Math.round(e.ratio * 100)}%`
  }
  if (e.type === 'ball_displayed') {
    if (e.machineId === 'red') {
      const red = [...shownRed]
      red[e.index] = e.number
      renderResultStrip(red, shownBlue)
    } else {
      const blue = [...shownBlue]
      blue[e.index] = e.number
      renderResultStrip(shownRed, blue)
    }
  }
  if (e.type === 'machine_finished') {
    // 用事件里的号码，避免 session.result 尚未写入导致清空红球
    if (e.machineId === 'red') {
      renderResultStrip(e.numbers, shownBlue)
    } else {
      renderResultStrip(shownRed, e.numbers)
    }
  }
  if (e.type === 'session_finished') {
    renderResultStrip(e.result.red, e.result.blue)
    pushHistory(e.result.red, e.result.blue)
  }
  if (e.type === 'error') {
    stateEl.textContent = `错误：${e.message}`
  }
  if (e.type === 'phase_change' && e.phase !== 'done') {
    // 切蓝球前确保红球结果已钉住
    if (e.phase === 'blue') {
      const s = controller.getSession()
      const red = s?.result.red?.length ? s.result.red : shownRed
      renderResultStrip(red, shownBlue)
    }
    setTab(e.phase)
  }
})

tabRed.addEventListener('click', () => setTab('red'))
tabBlue.addEventListener('click', () => setTab('blue'))
btnToggle.addEventListener('click', () => panel.classList.toggle('collapsed'))

btnStart.addEventListener('click', () => {
  try {
    idlePreview?.dispose()
    idlePreview = null
    progressEl.textContent = '0%'
    resultEl.textContent = '—'
    renderResultStrip([], [])
    controller.start()
    setTab('red')
    const world = controller.getActiveWorld()
    if (world) renderer.bindWorld(world)
  } catch (err) {
    stateEl.textContent = err instanceof Error ? err.message : String(err)
  }
})

btnCopy.addEventListener('click', () => openCopyModal())

btnHistory.addEventListener('click', () => openHistoryModal())

historyModal.querySelectorAll('[data-close-history-modal]').forEach((el) => {
  el.addEventListener('click', () => closeHistoryModal())
})

btnHistoryClear.addEventListener('click', () => {
  saveHistory([])
  renderHistoryList()
  showToast('历史已清空')
})

copyModal.querySelectorAll('[data-close-copy-modal]').forEach((el) => {
  el.addEventListener('click', () => closeCopyModal())
})

btnCopyPretty.addEventListener('click', async () => {
  const text = copyModalSource
    ? formatPrettyFrom(copyModalSource.red, copyModalSource.blue)
    : formatDrawnPretty()
  if (!text) {
    showToast('暂无已摇出的号码')
    return
  }
  const ok = await copyText(text)
  showToast(ok ? '复制成功' : '复制失败，请手动选择')
})

btnCopyCompact.addEventListener('click', async () => {
  const text = copyModalSource
    ? formatCompactFrom(copyModalSource.red, copyModalSource.blue)
    : formatDrawnCompact()
  if (!text) {
    showToast('暂无已摇出的号码')
    return
  }
  const ok = await copyText(text)
  showToast(ok ? '复制成功' : '复制失败，请手动选择')
})

btnReset.addEventListener('click', () => {
  controller.reset()
  stateEl.textContent = STATE_LABEL.Idle ?? 'Idle'
  progressEl.textContent = '—'
  resultEl.textContent = '—'
  renderResultStrip([], [])
  showIdlePreview()
})

setBootStatus('装载红球机台…')
const bootStartedAt = performance.now()
renderResultStrip([], [])
showIdlePreview()
stateEl.textContent = STATE_LABEL.Idle ?? 'Idle'
setBootStatus('准备首帧…')

let bootDismissed = false
let last = performance.now()
function frame(now: number): void {
  const dt = Math.min(0.05, (now - last) / 1000)
  last = now
  controller.tick(dt)
  const world = controller.getActiveWorld() ?? idlePreview
  if (world) {
    if (!controller.getActiveWorld() && idlePreview) {
      idlePreview.step(dt)
    }
    renderer.bindWorld(world)
    renderer.sync(world)
  }
  renderer.render()
  if (!bootDismissed) {
    const readyLongEnough = now - bootStartedAt >= 900
    if (readyLongEnough) {
      bootDismissed = true
      setBootStatus('就绪')
      requestAnimationFrame(() => dismissBootSplash())
    }
  }
  requestAnimationFrame(frame)
}
requestAnimationFrame(frame)
